import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Typography,
  Grid,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import Loading from '../../components/Loading';
import api from '../../services/api';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/auth';
import DiaryActivitiesTable from '../../components/DiaryActivitiesTable';
import UserDetails from '../../components/UserDetails';

const DiaryView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const [diary, setDiary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [publishingDiary, setPublishingDiary] = useState(false);
  const [confirmPublishOpen, setConfirmPublishOpen] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDiary = async () => {
      try {
        const response = await api.get(`/api/diary/${id}`);
        setDiary(response.data);
        setError(null);
      } catch (error) {
        console.error('Error fetching diary:', error);
        
        // Verificar se é um erro de permissão (403)
        if (error.status === 403 || (error.error && error.error === 'Forbidden')) {
          setError({
            type: 'forbidden',
            message: error.message || 'Você não tem permissão para acessar este diário'
          });
        } else {
          setError({
            type: 'general',
            message: 'Erro ao carregar diário'
          });
        }
        
        enqueueSnackbar(error.message || 'Erro ao carregar diário', { 
          variant: 'error',
          autoHideDuration: 5000
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDiary();
  }, [id, enqueueSnackbar]);

  const handlePublish = async () => {
    try {
      setPublishingDiary(true);
      await api.patch(`/api/diary/${id}/status`, { status: 'Published' });
      enqueueSnackbar('Diário publicado com sucesso', { variant: 'success' });
      // Atualiza os dados do diário para mostrar as informações de publicação
      const response = await api.get(`/api/diary/${id}`);
      setDiary(response.data);
    } catch (error) {
      console.error('Error publishing diary:', error);
      enqueueSnackbar(error.message || 'Erro ao publicar diário', { variant: 'error' });
    } finally {
      setPublishingDiary(false);
      setConfirmPublishOpen(false);
    }
  };

  const canEditDiary = () => {
    return user.profile === 'Administrador' || diary?.status === 'Draft';
  };

  if (loading) {
    return <Loading />;
  }

  // Se houver um erro de permissão, exibir mensagem amigável
  if (error && error.type === 'forbidden') {
    return (
      <Box p={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">Visualizar Diário</Typography>
          <Button onClick={() => navigate('/diary')}>
            Voltar para Lista de Diários
          </Button>
        </Box>
        
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
        
        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography variant="body1">
            Você não tem permissão para visualizar este diário. Isso pode ocorrer pelos seguintes motivos:
          </Typography>
          <ul>
            <li>O diário pertence a um lar ao qual você não está vinculado</li>
            <li>Você não é o profissional responsável por este diário</li>
            <li>Você não tem as permissões necessárias para acessar este recurso</li>
          </ul>
          <Typography variant="body1" mt={2}>
            Se você acredita que deveria ter acesso a este diário, entre em contato com o administrador do sistema.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Se houver um erro geral, exibir mensagem de erro
  if (error) {
    return (
      <Box p={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">Visualizar Diário</Typography>
          <Button onClick={() => navigate('/diary')}>
            Voltar para Lista de Diários
          </Button>
        </Box>
        
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
        
        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography variant="body1">
            Ocorreu um erro ao carregar os dados do diário. Por favor, tente novamente mais tarde.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const canPublish = canEditDiary() && diary?.status !== 'Published';

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4">Visualizar Diário</Typography>
          <Typography variant="subtitle1" color="textSecondary">
            ID: {id}
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button 
            onClick={() => navigate('/diary')} 
            sx={{ mr: 2 }}
          >
            Voltar
          </Button>
          {canPublish && (
            <Button
              onClick={() => setConfirmPublishOpen(true)}
              variant="contained"
              color="secondary"
              sx={{ 
                mr: 2,
                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                color: 'white',
                boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)'
              }}
            >
              Publicar
            </Button>
          )}
          {canEditDiary() && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(`/diary/${id}/edit`)}
              sx={{ mr: 1 }}
            >
              Editar
            </Button>
          )}
        </Box>
      </Box>

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={3}>
          <Box>
            <Typography variant="subtitle2" color="textSecondary">Lar</Typography>
            <Typography variant="body1">{diary.lar?.nome}</Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="textSecondary">Data de Criação</Typography>
            <Typography variant="body1">
              {new Date(diary.date_created).toLocaleDateString('pt-BR')}
            </Typography>
          </Box>

          <Box>
            <UserDetails 
              user={diary.professional} 
              label="Profissional"
              size={48}
            />
          </Box>

          <Box>
            <UserDetails 
              user={diary.resident} 
              label="Residente"
              size={48}
            />
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Atividades Registradas
        </Typography>
        
        <DiaryActivitiesTable activities={diary?.activities || []} />
      </Paper>

      <Accordion sx={{ mt: 2 }} defaultExpanded={false}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="ai-info-content"
          id="ai-info-header"
        >
          <Typography variant="h6">Informações de IA</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Texto-base pré-IA do Diário"
                value={diary.ai_summary_text_to_chatgpt || ''}
                fullWidth
                multiline
                rows={5}
                InputProps={{ readOnly: true }}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Resumo do Diário para o contato responsável pelo(a) residente"
                value={diary.ai_summary_text_from_chatgpt || ''}
                fullWidth
                multiline
                rows={5}
                InputProps={{ readOnly: true }}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Status de envio do Diário"
                value={diary.ai_summary_sent_log || ''}
                fullWidth
                multiline
                rows={5}
                InputProps={{ readOnly: true }}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Texto do Diário criado por IA enviado em"
                value={diary.ai_summary_sent_datetime || ''}
                fullWidth
                InputProps={{ readOnly: true }}
                variant="outlined"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Paper elevation={1} sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
        <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2}>
          <Box>
            <Typography variant="caption" color="textSecondary" display="block">
              Criado por
            </Typography>
            <Typography variant="body2">
              {diary.creator?.name}
              {diary.date_created && (
                <Typography component="span" variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                  em {new Date(diary.date_created).toLocaleString('pt-BR')}
                </Typography>
              )}
            </Typography>
          </Box>

          {diary.updated_by && (
            <Box>
              <Typography variant="caption" color="textSecondary" display="block">
                Atualizado por
              </Typography>
              <Typography variant="body2">
                {diary.updater?.name}
                {diary.date_updated && (
                  <Typography component="span" variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                    em {new Date(diary.date_updated).toLocaleString('pt-BR')}
                  </Typography>
                )}
              </Typography>
            </Box>
          )}

          {diary.published_by && (
            <Box>
              <Typography variant="caption" color="textSecondary" display="block">
                Publicado por
              </Typography>
              <Typography variant="body2">
                {diary.publisher?.name}
                {diary.date_published && (
                  <Typography component="span" variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                    em {new Date(diary.date_published).toLocaleString('pt-BR')}
                  </Typography>
                )}
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Diálogo de progresso durante a publicação */}
      <Dialog
        open={publishingDiary}
        onClose={() => {}}
        disableEscapeKeyDown
        PaperProps={{
          style: {
            backgroundColor: 'white',
            boxShadow: 'none',
            padding: '20px',
            minWidth: '300px',
            textAlign: 'center'
          },
        }}
      >
        <DialogTitle>Publicando Diário</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" py={2}>
            <CircularProgress size={60} thickness={4} sx={{ mb: 2 }} />
            <DialogContentText>
              Por favor, aguarde enquanto o diário é publicado...
            </DialogContentText>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Diálogo de confirmação de publicação */}
      <Dialog
        open={confirmPublishOpen}
        onClose={() => setConfirmPublishOpen(false)}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: '#f44336' }}>Atenção</DialogTitle>
        <DialogContent>
          <DialogContentText>
            As informações do diário foram revisadas e estão todas corretas? Diários publicados não podem ser alterados! Confirma a publicação?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmPublishOpen(false)} color="primary" variant="outlined">
            Cancelar
          </Button>
          <Button 
            onClick={handlePublish} 
            color="primary" 
            variant="contained"
            disabled={publishingDiary}
          >
            {publishingDiary ? 'Processando...' : 'Prosseguir'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DiaryView;
